/**
 * The bundle the embed page loads. RETIRE-WITH-PRICECALCULATOR.
 *
 * The only file in `apps/compat-2027/` that imports the guest widget at RUNTIME,
 * and the only one that imports `vergarde.ts`. Everything else here is either the
 * wire format or park-independent glue, which is what keeps the tests light: the
 * renderer's spec drives the real parent without pulling React-DOM, the guest
 * bundle or a fake API in behind it, because `ResultsView` arrives as a plain
 * object and the renderer's only outward edge is `bridge.send`.
 *
 * Imports reach exactly two places, both allowed by the boundary test:
 *
 *     apps/guest/src/widget.ts     the public widget surface
 *     ./                           this directory
 *
 * ---------------------------------------------------------------------------
 * WHY THE RESERVATION LINK COMES FROM `buildEnquiryLink`
 * ---------------------------------------------------------------------------
 *
 * Because there must not be a second implementation of that URL format. The
 * WordPress plugin, the guest app and this bridge all hand a visitor the same
 * deep link, and a format written twice drifts on the first parameter anybody
 * adds — which for the old site would mean a `Reserveren` link that silently
 * loses the dates the guest just chose.
 */

import {
  buildEnquiryLink, mountSearchWidget, type WidgetOptions,
} from '../../guest/src/widget.js';
import { createBridge } from './bridge.js';
import { createHeightReporter } from './height.js';
import { keyToAccommodationId } from './keys.js';
import { DUTCH_LABELS } from './labels.js';
import { postMessageResults } from './PostMessageResults.js';
import {
  CURRENCY, HOST_ORIGINS, kindOfConfig, LANG, LOCALE, TIME_ZONE,
} from './vergarde.js';

interface CompatConfig {
  config: string;
  kind: 'tariff-card' | 'tile-search';
  /**
   * `?redirect` from the host's URL. CARRIED BUT NOT ACTED ON.
   *
   * `compat-protocol.md` says it "affects where the finish step sends the
   * visitor", which is as much as the captured `widget.js` reveals — the parameter
   * is read by the OLD calculator's own page, which we are replacing, so there is
   * nothing left to compare against. The 2027 widget always deep-links to
   * `enquiryUrl`, so guessing at the old behaviour would be inventing one.
   *
   * It is threaded through anyway rather than dropped at the route, so that if the
   * old semantics turn out to matter, the value is already here and the fix is
   * three lines in this file instead of a change to the URL surface.
   */
  redirect: boolean;
  apiBase: string;
  parkId: string;
  locale: string;
  currency: string;
  /** Where a `Reserveren` link sends the guest. */
  enquiryUrl: string;
  hostOrigins: string[];
}

export function boot(doc: Document = document): void {
  const element = doc.getElementById('calculator');
  const payload = doc.getElementById('compat-config');
  if (!element || !payload) throw new Error('compat-2027: the embed page is missing its shell');
  const config = JSON.parse(payload.textContent ?? '{}') as CompatConfig;

  const money = new Intl.NumberFormat(config.locale || LOCALE, {
    style: 'currency',
    currency: config.currency || CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const options: WidgetOptions = {
    // No filters, and no outer edge.
    //
    // The park has twenty-four accommodations rather than four hundred, its
    // camping is an enquiry with no pitch to choose between, and this frame is
    // a fixed strip on a page whose layout is not ours — there is nowhere to
    // put a filter column and nothing much for it to do. It stays in the widget
    // for ES26 and the WordPress plugin, where a park's own page has room.
    //
    // `flush` because HolidayMedia's pages put no border on the iframe, so a
    // rounded card inside it draws a second edge just inside the first.
    showFilters: false,
    flush: true,
    /**
     * Paging on a phone too, against the convention and for a reason.
     *
     * Every major booking site scrolls the months on mobile inside a
     * full-screen dialog. Inside an iframe there is no full screen: "the
     * viewport" is the frame, a box on somebody else's page, and a scrolling
     * list in a short box is a nested scroller competing with the host page for
     * the same swipe. The operator hit exactly that — "I can't scroll in the
     * calendar to other months on mobile" — and before it, the same list with
     * no bound asked for a 2897px frame.
     *
     * A page is one month. It needs no gesture the host page also wants and no
     * height nobody chose.
     */
    monthNav: 'paged',
    apiBase: config.apiBase,
    parkId: config.parkId,
    locale: LANG,
    currency: config.currency || CURRENCY,
    // The park's clock, so the calendar's earliest arrival is the park's today. A
    // guest opening this at 00:30 would otherwise be offered yesterday.
    timeZone: TIME_ZONE,
    enquiryUrl: config.enquiryUrl,
  };

  /**
   * The part of the park this page is about, from the path it was asked for.
   *
   * DEFECT (reported): the rental page offered a dog. Its thirteen tiles are
   * thirteen rental units and none of them takes one, but the widget was
   * mounted unscoped, so the party picker read the whole catalogue — including
   * the campsite, which does. `kindOfConfig` carries the rest of the argument.
   *
   * Applied to BOTH embeds. The tariff card is already narrowed to one
   * accommodation by `accommodationId`, so this changes nothing there; setting
   * it anyway keeps the two paths from drifting into different answers to the
   * same question.
   */
  const kind = kindOfConfig(config.config);
  if (kind) options.kinds = [kind];

  /**
   * A tariff card has no host tiles to answer about — `/kampeer-tarieven-2026`
   * carries no `at_block-foto` and no markers at all — so it draws its own, which
   * is `tileResults`, the default. Only the search embed needs the bridge.
   *
   * It still needs a bridge for `scroll`, though: an unsized tariff card is
   * clipped at the host's 600px exactly like a clipped result list. So the bridge
   * is created either way; what differs is whether a renderer is passed.
   */
  const bridge = createBridge({ hostOrigins: config.hostOrigins ?? HOST_ORIGINS });
  const enquiryUrl = config.enquiryUrl;

  if (config.kind === 'tile-search') {
    /**
     * The ANSWERS live on the host page, so this frame draws none.
     *
     * DEFECT (reported): the rental embed rendered "Wat we kunnen aanbieden"
     * inside the iframe with nothing under it — because the heading and the
     * spinner were drawn unconditionally while the results themselves were
     * posted to the tiles the CMS had already put on the page below. A section
     * title over an empty box, with the prices it names sitting outside the
     * frame entirely.
     *
     * The renderer still runs; posting those prices is its whole job.
     */
    options.showResults = false;
    options.renderResults = postMessageResults({
      bridge,
      labels: DUTCH_LABELS,
      money: (amount) => money.format(amount),
      /**
       * Accommodation, arrival and length. Not the party — `ResultsView` does not
       * carry it, and adding it would be a change to a permanent seam for a
       * throwaway renderer. The enquiry page asks again, which is a form field the
       * guest re-enters rather than a wrong number they do not notice.
       */
      reservationUrl: (result, view) => buildEnquiryLink(enquiryUrl, {
        accommodationId: result.accommodation.id,
        ...(view.arrival === null ? {} : { arrival: view.arrival }),
        ...(view.nights === null ? {} : { nights: view.nights }),
        // Dutch travels with the guest. The page they came from is Dutch-only, so
        // letting the destination consult their browser would hand a Dutch reader
        // an English form for no reason anyone could see.
        lang: LANG,
      }),
    });
  } else {
    /**
     * A TARIFF CARD IS ABOUT ONE ACCOMMODATION, AND SAID SO NOWHERE.
     *
     * DEFECT (reported): "I know for sure, kamperen is not possible in February
     * 2027". It is not — the camping is shut from October to late March — and
     * the calendar on `/kampeer-tarieven` offered it anyway, because the widget
     * was mounted unscoped and answered for the whole park. February is open
     * for six caravans, so February looked open on the camping page.
     *
     * The scope is DERIVED from the config path by the same resolver the tile
     * markers use: `2027/kamperen/comfort` drops its year and joins to
     * `kamperen-comfort`, which is the accommodation's id. No table, no second
     * place for the park's catalogue to be written down and go stale — and the
     * one page that names an accommodation in its URL is exactly the page that
     * should be answering about that accommodation.
     *
     * Only for the card. A tile search is about the whole park by definition,
     * and its config path reduces to nothing that names anything.
     */
    options.accommodationId = keyToAccommodationId(config.config);

    // No renderer, so no effect to hang the measurement off. The frame still has
    // to be sized or the card is clipped at the host's 600px with no scrollbar.
    createHeightReporter((t, c, a) => bridge.send(t, c, a), () => doc);
  }

  mountSearchWidget(element, options);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
}
